/**
 * Single sink for all unexpected runtime errors that the app catches but
 * doesn't surface to the user as a normal flow.
 *
 * The privacy contract (SECURITY.md) is that nothing leaves the device,
 * so today this is just a thin wrapper around `console.error` (dev) or
 * a no-op (production). Routing through one helper means:
 *
 *  - any future telemetry sink lands here, behind a user opt-in;
 *  - silent catches in the codebase become greppable;
 *  - tests can spy on this single function.
 *
 * Tag is a stable short string identifying the call site, e.g.
 * "permalink.decode" or "dkim.verify". The error is the actual
 * exception; the optional context is anything that helps debug
 * (header name, attachment index, …) — never message content.
 */

function isDevMode(): boolean {
  // import.meta.env is provided by Vite at build time; guard for tests
  // and other contexts where it may not be available.
  try {
    const meta = import.meta as unknown as { env?: { DEV?: boolean } };
    return meta.env?.DEV === true;
  } catch {
    // Expected: `import.meta` access throws in non-ESM contexts (some
    // test envs, ts-node CJS). Treat as production — silent.
    return false;
  }
}

export function errorLog(
  tag: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!isDevMode()) return;
  console.error(`[${tag}]`, error, context ?? "");
}
